import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Product } from "@/types/product";
import { useToast } from "@/contexts/toast-context";
import { Badge } from "@/components/ui/badge";
import { Loader2 } from "lucide-react";

interface EditSaleDialogProps {
    product: Product | null;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onUpdate: (product: Product) => Promise<void> | void;
}

export function EditSaleDialog({ product, open, onOpenChange, onUpdate }: EditSaleDialogProps) {
    const { error: showError, success } = useToast();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [installmentError, setInstallmentError] = useState("");

    const [formData, setFormData] = useState({
        deviceName: "",
        deviceDescription: "",
        salePrice: "",
        monthlyInstallment: "",
        currency: "IQD" as "IQD" | "USD",
        date: "",
        notes: "",
    });

    // تازەکردنەوەی فیڵدەکان کاتێک product دەگۆڕێت
    useEffect(() => {
        if (product && open) {
            setFormData({
                deviceName: product.deviceName,
                deviceDescription: product.deviceDescription || "",
                salePrice: product.salePrice.toString(),
                monthlyInstallment: product.monthlyInstallment.toString(),
                currency: product.currency,
                date: product.date,
                notes: product.notes || "",
            });
            setInstallmentError("");
        }
    }, [product, open]);

    const handleInstallmentChange = (value: string) => {
        setFormData({ ...formData, monthlyInstallment: value });
        const installment = parseFloat(value) || 0;
        const salePrice = parseFloat(formData.salePrice) || 0;

        if (installment > salePrice) {
            setInstallmentError("قیستی مانگانە نابێت زیاتر بێت لە نرخی فرۆشتن");
        } else {
            setInstallmentError("");
        }
    };

    const handleSalePriceChange = (value: string) => {
        setFormData({ ...formData, salePrice: value });
        const salePrice = parseFloat(value) || 0;
        const installment = parseFloat(formData.monthlyInstallment) || 0;

        if (installment > salePrice) {
            setInstallmentError("قیستی مانگانە نابێت زیاتر بێت لە نرخی فرۆشتن");
        } else {
            setInstallmentError("");
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!product) return;

        if (!formData.deviceName || !formData.salePrice || !formData.monthlyInstallment) {
            showError("تکایە هەموو فیڵدە پێویستەکان پڕبکەوە");
            return;
        }

        const newSalePrice = parseFloat(formData.salePrice);
        const newMonthlyInstallment = parseFloat(formData.monthlyInstallment);

        if (newMonthlyInstallment > newSalePrice) {
            setInstallmentError("قیستی مانگانە نابێت زیاتر بێت لە نرخی فرۆشتن");
            return;
        }

        // ئەگەر پارەدان کراوە، ناتوانرێت نرخ کەمتر بکرێت لە پارەی دراو
        if (newSalePrice < product.paidAmount) {
            showError(`نرخی فرۆشتن ناتوانێت کەمتر بێت لە پارەی دراو (${product.paidAmount.toLocaleString()})`);
            return;
        }

        setIsSubmitting(true);
        try {
            // حیسابکردنی remainingAmount ی نوێ
            const newRemainingAmount = newSalePrice - product.paidAmount;

            const updatedProduct: Product = {
                ...product,
                deviceName: formData.deviceName,
                deviceDescription: formData.deviceDescription,
                salePrice: newSalePrice,
                monthlyInstallment: newMonthlyInstallment,
                currency: formData.currency,
                date: formData.date,
                notes: formData.notes,
                remainingAmount: newRemainingAmount,
            };

            await onUpdate(updatedProduct);
            success("فرۆشتن نوێکرایەوە");
            onOpenChange(false);
        } catch {
            showError("هەڵەیەک ڕوویدا لە نوێکردنەوەی فرۆشتن");
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!product) return null;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle>دەستکاریکردنی فرۆشتن</DialogTitle>
                    <DialogDescription>زانیاریەکانی فرۆشتنەکە دەستکاری بکە</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* زانیاری پێشوو */}
                    <div className="flex flex-wrap gap-2 p-3 rounded-lg bg-muted/50 border">
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground">پارەی دراو:</span>
                            <Badge variant="secondary" className="text-xs">
                                {product.currency === "USD" && "$"}
                                {product.paidAmount.toLocaleString()}
                                {product.currency === "IQD" && " د.ع"}
                            </Badge>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground">ژمارەی قیست:</span>
                            <Badge variant="outline" className="text-xs">
                                {product.installments || 0}
                            </Badge>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="deviceName">ناوی ئامێر *</Label>
                            <Input
                                id="deviceName"
                                value={formData.deviceName}
                                onChange={(e) => setFormData({ ...formData, deviceName: e.target.value })}
                                required
                                placeholder="ناوی ئامێر"
                                disabled={isSubmitting}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="deviceDescription">وەسفی ئامێر</Label>
                            <Input
                                id="deviceDescription"
                                value={formData.deviceDescription}
                                onChange={(e) => setFormData({ ...formData, deviceDescription: e.target.value })}
                                placeholder="وەسفی ئامێر"
                                disabled={isSubmitting}
                            />
                        </div>
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="currency">دراو</Label>
                        <Input
                            id="currency"
                            value={formData.currency === "IQD" ? "دینار (IQD)" : "دۆلار (USD)"}
                            disabled
                            className="bg-muted"
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="salePrice">نرخی فرۆشتن *</Label>
                            <Input
                                id="salePrice"
                                type="number"
                                value={formData.salePrice}
                                onChange={(e) => handleSalePriceChange(e.target.value)}
                                required
                                placeholder="0"
                                dir="ltr"
                                disabled={isSubmitting}
                            />
                            {product.paidAmount > 0 && (
                                <p className="text-xs text-amber-600">
                                    کەمترین نرخ: {product.paidAmount.toLocaleString()}
                                </p>
                            )}
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="monthlyInstallment">قیستی مانگانە *</Label>
                            <Input
                                id="monthlyInstallment"
                                type="number"
                                value={formData.monthlyInstallment}
                                onChange={(e) => handleInstallmentChange(e.target.value)}
                                required
                                placeholder="0"
                                dir="ltr"
                                className={installmentError ? "border-destructive" : ""}
                                disabled={isSubmitting}
                            />
                            {installmentError && (
                                <p className="text-sm text-destructive">{installmentError}</p>
                            )}
                        </div>
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="date">بەروار</Label>
                        <Input
                            id="date"
                            type="date"
                            value={formData.date}
                            onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                            dir="ltr"
                            disabled={isSubmitting}
                        />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="notes">تێبینی</Label>
                        <Textarea
                            id="notes"
                            value={formData.notes}
                            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                            placeholder="تێبینی..."
                            rows={2}
                            disabled={isSubmitting}
                        />
                    </div>
                    <div className="flex gap-2 justify-end pt-4">
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
                            پاشگەزبوونەوە
                        </Button>
                        <Button type="submit" disabled={!!installmentError || isSubmitting}>
                            {isSubmitting ? (
                                <>
                                    <Loader2 className="h-4 w-4 animate-spin ml-2" />
                                    چاوەڕوان بە...
                                </>
                            ) : (
                                "پاشەکەوتکردن"
                            )}
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
}
